import { useCallback, useMemo, useState } from 'react';
import type { SettableLineApprovalState } from '@erp/rpc';
import { T, Var, useGT } from 'gt-react';
import { toast } from '@/components/ui/toast';
import type { ColumnDef } from '@/lib/tanstack-table';
import { cn } from '@/lib/utils';
import { DataTableColumnHeader } from '@/components/common/data-table/data-table-column-header';
import {
  useSetSuggestionStatus,
  useSetSuggestionStatuses,
  useApplyOption,
  useSetSuggestionApprovalState,
  type AssignmentLine,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { ProductCell, ProductDescriptionCell } from './ProductCell';
import { idBatches } from '../utils/idBatches';
import {
  getLineDesignation,
  isPotfLine,
  sumPoValue,
} from '../utils/lineFilters';
import { ExpectedArrival } from './LeadTimeDetail';
import { DesignationBadge } from './DesignationBadge';
import { ApprovalStateCell } from './ApprovalStateCell';
import { CostCell } from './CostCell';
import { UnitCostCell } from './UnitCostCell';
import { QuantityCell } from './QuantityCell';
import { SourceCell } from './SourceCell';
import { CalcQuantityCell } from './quantityOverrideCells';
import { BelowVendorMinBadge } from './BelowVendorMinBadge';
import { TriggerCell } from './TriggerCell';
import { SELECTED_ROW_CLASS } from './selectedRowClass';
import { isLineGroupingOn } from './lineGroupTable';
import { getCartLineTableMeta as getMeta } from './cartLineTableMeta';
import { getDivergentShipToName } from './divergentShipTo';
import { effectiveQuantity, quantityDiff } from '../utils/lineQuantity';
import { approvalTargetPlan } from '../utils/approvalTargetPlan';
import { weightHoverTitle } from '../utils/lineWeight';
import { stockFlag } from '../utils/stockFlag';
import type { LineBulkEditTargets } from './lineBulkEditContext';

export type { CartLineTableMeta } from './cartLineTableMeta';

const priorityColors: Record<string, string> = {
  critical: 'bg-red-200 text-red-800',
  urgent: 'bg-red-100 text-red-700',
  standard: 'bg-blue-100 text-blue-700',
  fill: 'bg-gray-100 text-gray-700',
};

/**
 * Shared trash/restore state for a cart-line table — wires the
 * setSuggestionStatus mutation + tracks in-flight ids so the row button can
 * disable while pending. Used by both the supplier and transfer boards.
 */
export function useTrashLine() {
  const setStatus = useSetSuggestionStatus();
  const [pendingStatusIds, setPendingStatusIds] = useState<Set<string>>(
    new Set(),
  );
  const handleSetStatus = useCallback(
    (suggestionId: string, status: 'pending' | 'rejected') => {
      setPendingStatusIds((prev) => new Set(prev).add(suggestionId));
      setStatus.mutate(
        { suggestionId, status },
        {
          onSettled: () => {
            setPendingStatusIds((prev) => {
              const next = new Set(prev);
              next.delete(suggestionId);
              return next;
            });
          },
        },
      );
    },
    [setStatus],
  );
  const setStatuses = useSetSuggestionStatuses();
  const gt = useGT();
  // React Compiler memoizes this; no manual useCallback.
  const handleSetStatuses = (
    suggestionIds: readonly string[],
    status: 'pending' | 'rejected',
  ) => {
    setPendingStatusIds((prev) => new Set([...prev, ...suggestionIds]));
    // One call per batch; each clears its own ids when it settles (mutate's
    // per-call callbacks would only run for the last batch).
    for (const batch of idBatches(suggestionIds)) {
      void setStatuses
        .mutateAsync({ suggestionIds: batch, status })
        .catch(() => {
          toast.error(
            gt('Some lines could not be updated. Try again.', {
              context:
                'replenishment board: a bulk trash or restore failed for part of the selected lines',
            }),
          );
        })
        .finally(() => {
          setPendingStatusIds((prev) => {
            const next = new Set(prev);
            for (const id of batch) next.delete(id);
            return next;
          });
        });
    }
  };
  return { handleSetStatus, handleSetStatuses, pendingStatusIds };
}

/**
 * A board's lines with duplicate suggestions dropped (a suggestion can arrive
 * once per cart it touches), plus the buyer's approvals over them.
 */
export function useBoardCartLines(
  rawLines: AssignmentLine[] | undefined,
  editTargets: LineBulkEditTargets,
) {
  const lines = useMemo(() => {
    const seen = new Set<string>();
    return (rawLines ?? []).filter((l) => {
      if (seen.has(l.suggestionId)) return false;
      seen.add(l.suggestionId);
      return true;
    });
  }, [rawLines]);
  const { selectedIds, onSetApproval } = useLineApproval(lines, editTargets);
  return { lines, selectedIds, onSetApproval };
}

/**
 * The buyer's approvals for a board. The server is the source of truth: the
 * approved set is the lines whose `approvalState` is `approved`, which
 * persists on the suggestion, so lines start unapproved and survive a reload.
 * Changes write through `setSuggestionApprovalState`, whose optimistic cache
 * patch flips the badge instantly and rolls back on error. Only approved lines
 * are released; a Trouble line never is.
 *
 * Approving a never-committed fill candidate also fires `applyOption`, so the
 * line shows committed on its top option like an engine-committed line. That
 * call is fire-and-forget; the approval is what drives release.
 */
export function useLineApproval(
  lines: AssignmentLine[],
  editTargets: LineBulkEditTargets,
) {
  const applyOption = useApplyOption();
  const setApprovalState = useSetSuggestionApprovalState();

  const linesById = new Map(lines.map((line) => [line.suggestionId, line]));

  // React Compiler memoizes this.
  const selectedIds = new Set<string>();
  for (const line of lines) {
    if (
      line.approvalState === 'approved' ||
      line.approvalState === 'auto_approved'
    ) {
      selectedIds.add(line.suggestionId);
    }
  }

  // React Compiler memoizes this; no manual useCallback.
  const onSetApproval = (
    line: AssignmentLine,
    state: SettableLineApprovalState,
  ) => {
    const targetIds = editTargets(line.suggestionId);
    const suggestionIds: string[] = [];
    for (const suggestionId of targetIds) {
      const plan = approvalTargetPlan(linesById.get(suggestionId), state);
      if (plan.kind === 'skip') continue;
      if (plan.kind === 'commit') {
        applyOption.mutate({ suggestionId, optionId: plan.optionId });
      }
      suggestionIds.push(suggestionId);
    }
    if (suggestionIds.length === 0) return;
    for (const batch of idBatches(suggestionIds)) {
      setApprovalState.mutate({ suggestionIds: batch, state });
    }
  };

  return { selectedIds, onSetApproval };
}

/**
 * The buyer's live cart selection, surfaced to cells through React context
 * instead of (only) the table `meta`. TanStack reuses a stable `table` instance
 * and memoizes cell contexts, so a cell that reads selection state off `meta`
 * is NOT guaranteed to re-render when that `Set` changes — the cart-toggle icon
 * would visibly lag the selection while the row's accent (read directly in
 * `DataTable` via `getRowClassName`) updated. A context consumer always
 * re-renders on value change, so the toggle's color/fill now tracks the
 * selection reliably. Boards wrap their `DataTable` in this provider.
 */
/** Is the line currently checked into the cart? */
export function isLineIncluded(
  line: AssignmentLine,
  selectedIds: Set<string>,
): boolean {
  return selectedIds.has(line.suggestionId);
}

export function isSoLine(line: AssignmentLine): boolean {
  return line.trigger === 'sales_order';
}

/**
 * "Purchase involved" = the engine's top option ends up buying from a vendor,
 * either pure buy or buy-plus-transfer (pivot). Pure transfers don't qualify.
 */
export function involvesPurchase(line: AssignmentLine): boolean {
  const action = line.topOption?.action;
  return action === 'buy' || action === 'buy_and_transfer';
}

/**
 * Row styles distinguish included lines from optional candidates and rejected
 * lines without adding a separate status column.
 */
export function getRowAccentClass(
  line: AssignmentLine,
  included = false,
  selected = false,
  bulkHighlighted = false,
): string {
  // The line open in the Line section: the Orders table's selected look, which
  // replaces the designation tint on this one row.
  if (selected) return SELECTED_ROW_CLASS;
  if (bulkHighlighted) {
    return 'bg-sky-100 shadow-[inset_3px_0_0_0_hsl(var(--primary))] dark:bg-sky-950/45';
  }
  // Trashed (rejected) rows — only visible with "Show trashed" on. Dim them
  // so they read as set-aside, with a Restore action in the row.
  if (line.status === 'rejected') return 'bg-gray-50 opacity-60';
  // Released since the last refresh (GEN-3119): kept for reference, read-only.
  if (line.approvalState === 'released') return 'bg-gray-50 opacity-70';
  // Candidates stay warm and subdued until the buyer includes them.
  if (!included) return 'bg-amber-50/40';
  // A purchase gets a quiet blue tint; pure transfers remain neutral. SO vs.
  // min/max distinction lives in the Trigger badge column.
  return involvesPurchase(line) ? 'bg-blue-50/40' : 'bg-gray-50';
}

function WarehouseTypeBadge({ type }: { type: string | null }) {
  return (
    <span
      className={cn(
        'rounded px-1 py-0.5 text-xs font-medium leading-none shrink-0',
        type === 'hub'
          ? 'bg-indigo-100 text-indigo-700'
          : 'bg-gray-200 text-gray-500',
      )}
    >
      {type ?? 'WH'}
    </span>
  );
}

/**
 * Buy cost for the line at a glance: the chosen/top option's total cost for a
 * purchase, and $0 for a pure transfer (no vendor spend). Buy+transfer shows
 * the buy portion (the option's totalCost). Sortable.
 */
export const unitCostColumn: ColumnDef<AssignmentLine> = {
  id: 'unitCost',
  size: 90,
  accessorFn: (l) => l.topOption?.unitCost ?? null,
  meta: { align: 'right' },
  header: ({ column, table }) => {
    const meta = getMeta(table);
    return (
      <DataTableColumnHeader
        column={column}
        title={meta.gt('Unit cost', {
          context: 'cart line table column header for buy unit cost',
        })}
      />
    );
  },
  cell: ({ row, table }) => <UnitCostCell row={row} table={table} />,
};

export const costColumn: ColumnDef<AssignmentLine> = {
  id: 'cost',
  size: 90,
  accessorFn: (l) => sumPoValue([l]),
  meta: { align: 'right' },
  header: ({ column, table }) => {
    const meta = getMeta(table);
    return (
      <DataTableColumnHeader
        column={column}
        title={meta.gt('Cost', {
          context: 'table column header for line cost',
        })}
      />
    );
  },
  cell: ({ row, table }) => <CostCell row={row} table={table} />,
};

// Sort order for the state column: what needs a buyer first.
const approvalStateRank: Record<AssignmentLine['approvalState'], number> = {
  unapproved: 0,
  trouble: 1,
  approved: 2,
  auto_approved: 2,
  released: 3,
  rejected: 4,
};

export const cartLineColumns: ColumnDef<AssignmentLine>[] = [
  // The line's approval state as a fixed-width shorthand code, right of Qty
  // in the standard layout.
  {
    id: 'approvalState',
    accessorFn: (l) => approvalStateRank[l.approvalState],
    // Numeric accessors sort descending first; the first click should put the
    // lines that need a buyer (Unapproved) on top.
    sortDescFirst: false,
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('State', {
            context: 'cart line table column header, line approval state',
          })}
        />
      );
    },
    cell: ({ row, table }) => <ApprovalStateCell row={row} table={table} />,
    size: 56,
  },
  {
    id: 'warehouse',
    accessorFn: (l) => l.destinationWarehouseName,
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Warehouse', {
            context: 'cart line table column header',
          })}
        />
      );
    },
    cell: ({ row, table }) => {
      const line = row.original;
      const shipToName = getDivergentShipToName(
        line,
        getMeta(table).cartsByShipTo,
      );
      return (
        <span className="flex min-w-0 flex-col">
          <span className="flex items-center gap-1 truncate">
            <WarehouseTypeBadge type={line.destinationWarehouseType} />
            <span className="truncate text-gray-500">
              {line.destinationWarehouseName}
            </span>
          </span>
          {shipToName && (
            <span className="truncate text-[11px] text-gray-400">
              <T context="cart line warehouse cell ship-to secondary text">
                Ship to <Var>{shipToName}</Var>
              </T>
            </span>
          )}
        </span>
      );
    },
  },
  // One location column: Ship-to and Warehouse are the same warehouse except
  // on a pivot, where the ship-to renders as secondary text above.
  // No Hub column: the warehouse cell already badges hub-vs-branch, the board
  // has a Hub filter, and the parent hub shows on the need row / detail blade.
  {
    id: 'product',
    accessorFn: (l) => l.productSku ?? l.productName,
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Item', {
            context: 'cart line item column header, the SKU',
          })}
        />
      );
    },
    cell: ({ row }) => <ProductCell line={row.original} />,
  },
  {
    id: 'description',
    accessorFn: (l) => l.productDescription ?? '',
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Description', {
            context: 'cart line table column header, the item description',
          })}
        />
      );
    },
    cell: ({ row }) => <ProductDescriptionCell line={row.original} />,
  },
  // The triggering sales order number on its own, highlighted like the SO
  // trigger badge; the column header carries the "SO".
  {
    id: 'salesOrder',
    size: 80,
    accessorFn: (l) => l.triggerSalesOrderNumber ?? '',
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('SO', {
            context: 'cart line table column header, sales order number',
          })}
        />
      );
    },
    cell: ({ row }) => {
      const number = row.original.triggerSalesOrderNumber;
      return number ? (
        <span className="rounded bg-red-100 px-1.5 py-0.5 font-mono text-xs font-medium text-red-700">
          {number}
        </span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    },
  },
  // From: the vendor, or a location to transfer from (GEN-3023, GEN-3026).
  {
    id: 'source',
    accessorFn: (l) =>
      l.topOption?.action === 'transfer'
        ? (l.topOption.sourceWarehouseName ?? '')
        : '',
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('From', {
            context:
              'cart line table column header, ship-from vendor or location',
          })}
        />
      );
    },
    cell: ({ row, table }) => <SourceCell row={row} table={table} />,
  },
  {
    id: 'quantity',
    size: 96,
    accessorFn: (l) => effectiveQuantity(l),
    meta: { align: 'right' },
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Qty', { context: 'cart line table column header' })}
        />
      );
    },
    cell: ({ row, table }) => (
      <span
        className="inline-flex flex-row-reverse items-center gap-1"
        title={weightHoverTitle(
          row.original.unitWeightLbs,
          effectiveQuantity(row.original),
          getMeta(table).gt,
        )}
      >
        <QuantityCell row={row} table={table} />
        <BelowVendorMinBadge
          compact
          buyQuantity={row.original.topOption?.buyQuantity}
          vendorMoqQuantity={row.original.topOption?.vendorMoqQuantity}
        />
      </span>
    ),
  },
  {
    id: 'calcQuantity',
    size: 70,
    accessorFn: (l) => quantityDiff(l).calculated,
    meta: { align: 'right' },
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Calc', {
            context:
              'cart line table column header, engine calculated quantity',
          })}
        />
      );
    },
    cell: ({ row }) => <CalcQuantityCell line={row.original} />,
  },
  {
    id: 'pop',
    size: 56,
    accessorFn: (l) => l.popularityCode ?? '',
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Pop', { context: 'cart line table column header' })}
        />
      );
    },
    cell: ({ row }) => {
      // Plain text, not a chip — the code is a loose AS400 string with no
      // validated vocabulary. See components/procurement/PopularityCell.tsx.
      const code = row.original.popularityCode;
      return code ? (
        <span className="font-medium text-gray-600">{code}</span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    },
  },
  {
    id: 'stock',
    size: 60,
    accessorFn: (l) => l.stockStatus ?? '',
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Stock', { context: 'cart line table column header' })}
        />
      );
    },
    cell: ({ row }) => {
      const flag = stockFlag(row.original.stockStatus);
      return flag ? (
        <span
          className="font-medium text-gray-600"
          title={row.original.stockStatus ?? undefined}
        >
          {flag}
        </span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    },
  },
  {
    id: 'trigger',
    size: 80,
    accessorFn: (l) => (isSoLine(l) ? 0 : 1),
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Trigger', {
            context: 'cart line table column header',
          })}
        />
      );
    },
    // POTF lines sit in their own group; mark them per line only when a sort
    // has turned the group headers off.
    cell: ({ row, table }) => (
      <TriggerCell
        row={row}
        showPotf={!isLineGroupingOn(table) && isPotfLine(row.original)}
      />
    ),
  },
  {
    id: 'designation',
    size: 70,
    accessorFn: (l) => getLineDesignation(l) ?? '',
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Plan', { context: 'cart line table column header' })}
        />
      );
    },
    cell: ({ row }) => {
      const line = row.original;
      const designation = getLineDesignation(line);
      if (!designation) {
        return <span className="text-gray-300">—</span>;
      }
      return <DesignationBadge designation={designation} />;
    },
  },
  {
    id: 'leadTime',
    size: 80,
    // Sort measured/estimated first, no-estimate rows last.
    accessorFn: (l) =>
      l.topOption?.estimatedLeadTimeDays ?? Number.MAX_SAFE_INTEGER,
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Arrival', {
            context: 'cart line table column header',
          })}
        />
      );
    },
    cell: ({ row, table }) => {
      const line = row.original;
      const meta = getMeta(table);
      // Transfer transit is a fixed assumption, not a measurement — show
      // nothing rather than pass it off as an estimate.
      const isTransfer = line.topOption?.action === 'transfer';
      return (
        <ExpectedArrival
          days={
            isTransfer ? null : (line.topOption?.estimatedLeadTimeDays ?? null)
          }
          overrideDays={
            isTransfer ? null : (line.topOption?.leadTimeOverrideDays ?? null)
          }
          productLabel={line.productSku}
          // productSku carries the ProductV3 key (the SKU) on v3 rows and the
          // v2 SKU (same AS400 key space) otherwise; productId is the v2
          // fallback lookup.
          productV3Key={line.productSku}
          warehouseId={line.destinationWarehouseId}
          suggestionId={line.suggestionId}
          highlightCartKey={meta.highlightCartKey}
        />
      );
    },
  },
  {
    id: 'priority',
    size: 84,
    accessorFn: (l) => l.priority,
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Priority', {
            context: 'cart line table column header',
          })}
        />
      );
    },
    cell: ({ row }) => (
      <span
        className={`rounded-full px-2 py-0.5 text-xs font-medium truncate inline-block ${
          priorityColors[row.original.priority] ?? 'bg-gray-100 text-gray-700'
        }`}
      >
        {row.original.priority}
      </span>
    ),
  },
  // Per line (item x location): off to the right, for spotting a mis-coded item.
  {
    id: 'buyer',
    size: 72,
    accessorFn: (l) => l.buyerCode ?? '',
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Buyer', { context: 'cart line table column header' })}
        />
      );
    },
    cell: ({ row }) => {
      const code = row.original.buyerCode;
      return code ? (
        <span className="font-mono text-xs text-gray-600">{code}</span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    },
  },
  // The core-offer program the item sits in at this location (GEN-3151).
  {
    id: 'coreOffer',
    size: 120,
    accessorFn: (l) => l.coreOffer ?? '',
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Core offer', {
            context: 'cart line table column header, core-offer program label',
          })}
        />
      );
    },
    cell: ({ row }) => {
      const label = row.original.coreOffer;
      return label ? (
        <span className="truncate text-xs text-gray-600" title={label}>
          {label}
        </span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    },
  },
];
